import {useCallback, useEffect, useRef} from 'react';
import utils from '../helpers/utils';

/**
 * Custom hook to handle user activity tracking and NGP keep-alive
 * @returns User activity related methods
 */
export const useUserActivity = () => {
  const lastActiveRef = useRef<number>(Date.now());
  const ngpKeepAliveRef = useRef<NodeJS.Timer | null>(null);

  /**
   * Updates the last active timestamp on user activity
   */
  const handleUserActivity = useCallback(() => {
    lastActiveRef.current = Date.now();
  }, []);

  /**
   * Handles Yodlee iframe activity
   */
  const handleYodleeIframeActivity = useCallback(
    (msg: any) => {
      if (utils.safeGet(msg.data, 'fnToCall', null) === 'renewClientSession') {
        handleUserActivity();
      }
    },
    [handleUserActivity]
  );

  /**
   * Sets up the NGP keep-alive mechanism
   */
  const setupNgpKeepAlive = useCallback(() => {
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('keypress', handleUserActivity);
    window.addEventListener('message', handleYodleeIframeActivity);

    // Checks every 60s for new activity. Notifies NGP if new activity found.
    const timer = setInterval(() => {
      if (Date.now() - lastActiveRef.current < 60000) {
        // last activity was less than 60 seconds ago
        const sendMessage = (message: any, targetOrigin: string) => {
          if (window.parent && window.parent !== window) {
            window.parent.postMessage(message, targetOrigin);
          }
        };
        sendMessage({type: 'KEEP_NGP_ALIVE', payload: ''}, window.REACT_APP_PARENT_HOST);
      }
    }, 60000);

    ngpKeepAliveRef.current = timer;
    return timer;
  }, [handleUserActivity, handleYodleeIframeActivity]);

  /**
   * Tears down the NGP keep-alive mechanism
   */
  const teardownNgpKeepAlive = useCallback(() => {
    window.removeEventListener('click', handleUserActivity);
    window.removeEventListener('keypress', handleUserActivity);
    window.removeEventListener('message', handleYodleeIframeActivity);

    if (ngpKeepAliveRef.current) {
      clearInterval(ngpKeepAliveRef.current);
      ngpKeepAliveRef.current = null;
    }
  }, [handleUserActivity, handleYodleeIframeActivity]);

  /**
   * Setup the NGP keep-alive when the component mounts and tear it down when it unmounts
   */
  useEffect(() => {
    const timer = setupNgpKeepAlive();

    return () => {
      if (timer) {
        clearInterval(timer);
      }
      teardownNgpKeepAlive();
    };
  }, [setupNgpKeepAlive, teardownNgpKeepAlive]);

  return {
    handleUserActivity,
    handleYodleeIframeActivity,
    setupNgpKeepAlive,
    teardownNgpKeepAlive,
  };
};
